import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "moment-timezone";
import useWebSocket from "react-use-websocket";
import {
  findGetParameter,
  getData,
  getWSAuth,
  newPath,
  useInterval
} from "../../../../components/utilities/UtilityFunctions";
import EventPlacingsDefault from "./EventPlacingsDefault";
import { postNotification } from "../../../../components/utilities/Notifications";
import { handlePagingAll } from "../../../../components/api/APIUtilities";
import { getWebsocketUrl } from "../EventFunctions";

const WEBSOCKET_URL = getWebsocketUrl();

export default function EventPlacingsWS(props) {
  const {
    organize,
    activeTab,
    setActiveTab,
    event,
    setEvent,
    authContext,
    setResetModalOpen
  } = props;
  const navigate = useNavigate();

  var leagueId = findGetParameter("league");
  if (!leagueId) leagueId = findGetParameter("circuit");

  const [originalLoading, setOriginalLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  const [playerType, setPlayerType] = useState(
    event?.teamEvent ? "teamplayers" : "players"
  );

  const [players, setPlayers] = useState(null);
  const [playerDict, setPlayerDict] = useState({});
  const [player, setPlayer] = useState(null);
  const [showOverall, setShowOverall] = useState(false);
  const [placingDict, setPlacingDict] = useState({});

  const [league, setLeague] = useState(null);

  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [refreshPlayers, setRefreshPlayers] = useState(false);

  const [placingsReady, setPlacingsReady] = useState(false);

  const [pTypeUpdating, setPTypeUpdating] = useState(true);

  const [socketStatus, setSocketStatus] = useState("Connecting");
  const [socketUrl, setSocketUrl] = useState(null);
  const webSocket = useWebSocket(socketUrl, {
    share: true,
    onOpen: () => {
      setPTypeUpdating(false);
      setSocketStatus("Live");
    },
    onClose: () => {
      if (!pTypeUpdating) {
        setSocketStatus("Disconnected");
      }
    }
  });

  // Checks if placings are up to date
  const checkStatus = async () => {
    if (event) {
      try {
        const apiStatus = await getData(`/events/${event.id}/updatestatus`);
        if (apiStatus?.status === "ready") {
          setPlacingsReady(true);
        }
        if (apiStatus?.status === "processing") {
          setPlacingsReady(false);
        }
      } catch (err) {
        setPlacingsReady(false);
      }
    }
  };

  const sortPlayers = (playerArray) => {
    playerArray.sort((a, b) => {
      if (showOverall) {
        if (a.overallPlacing && !b.overallPlacing) return -1;
        if (!a.overallPlacing && b.overallPlacing) return 1;
        return a.overallPlacing - b.overallPlacing;
      }

      if (a.podNum && !b.podNum) return -1;
      if (!a.podNum && b.podNum) return 1;
      if (a.podNum < b.podNum) return -1;
      if (a.podNum > b.podNum) return 1;
      if (a.isPod && !b.isPod) return -1;
      if (!a.isPod && b.isPod) return 1;

      return a.placing - b.placing;
    });
    return playerArray;
  };

  const handleLoad = async (collection) => {
    try {
      const fetchPlayers = async () => {
        const playerArray = await getData(
          `/events/${event?.id}/${
            collection ? collection.toLowerCase() : "/players"
          }`,
          {
            placings: true
          }
        );

        playerArray?.active?.sort((a, b) => {
          if (a.placing > b.placing) {
            return -1;
          }
          if (a.placing < b.placing) {
            return 1;
          }
          return 0;
        });

        playerArray?.active?.forEach((p) => {
          if (p.softScores) {
            var softScores = {};
            p.softScores.forEach((softScore) => {
              softScores[softScore.name] = softScore;
            });
            p.softScores = softScores;
          }
        });

        return playerArray?.active;
      };

      const fetchPlacings = async () => {
        if (!leagueId) return [];
        return await handlePagingAll("/eventplacings", {
          eventId: event.id,
          leagueId: leagueId,
          "expand[]": ["user", "team", "army", "subFaction", "character"]
        });
      };

      const fetchLeague = async () => {
        if (!leagueId) return null;
        var returnObj;
        try {
          returnObj = await getData(`/leagues/${leagueId}`);
        } catch (err) {
          console.error(err);
        }
        return returnObj;
      };

      async function parallel() {
        const taskPlayers = fetchPlayers();
        const taskPlacings = fetchPlacings();
        const taskLeague = fetchLeague();

        return {
          players: await taskPlayers,
          placings: await taskPlacings,
          league: await taskLeague
        };
      }

      var parallelData = await parallel();
      if (parallelData.league) setLeague(parallelData.league);

      // Adds team info for individual players in team/doubles events
      if (
        parallelData?.players?.length &&
        playerType === "players" &&
        (event?.doublesEvent || event?.teamEvent)
      ) {
        let apiTeams = null;
        try {
          apiTeams = await getData(`/events/${event?.id}/teamPlayers`, {
            placings: false
          });
        } catch (err) {
          postNotification("error");
        }

        if (apiTeams?.active) {
          parallelData.players.forEach((player) => {
            apiTeams.active.forEach((team) => {
              if (player?.teamPlayerId === team?.id) {
                player.team = { ...team };
              }
            });
          });
        }
      }

      var newPods = {};
      var newPlayerDict = {};
      parallelData.players.forEach((p) => {
        if (
          (event.topCut || event.bracketPairings) &&
          ((p.podNum === undefined && !newPods["undefined"]) ||
            !newPods[p.podNum])
        ) {
          newPods[p.podNum !== undefined ? p.podNum : "undefined"] = {
            isPod: true,
            podNum: p.podNum
          };
        }
        newPlayerDict[p.id] = p;
      });

      if (Object.keys(newPods).length > 0) {
        if (event.topCut) {
          if (newPods[1]) {
            newPods[1].label = event.doubleElimination
              ? "Championship Bracket"
              : "Top Cut";
          }
          if (event.doubleElimination) {
            if (newPods[2]) {
              newPods[2].label = "Elimination Bracket";
            }
            if (newPods[3]) {
              newPods[3].label = "Eliminated Bracket";
            }
          }
        } else if (
          event.pairingStyle === "single elimination" ||
          event.pairingStyle === "double elimination"
        ) {
          if (newPods[1]) {
            newPods[1].label = "Championship Bracket";
          }
          if (newPods[2]) {
            newPods[2].label =
              event.pairingStyle === "single elimination"
                ? "Eliminated Bracket"
                : "Elimination Bracket";
          }
          if (newPods[3]) {
            newPods[3].label = "Eliminated Bracket";
          }
        }

        parallelData.players.push(...Object.values(newPods));
      }

      setPlayerDict(newPlayerDict);
      setPlayers(sortPlayers(parallelData.players));

      if (parallelData.placings?.length > 0) {
        var newPlacingDict = {};
        parallelData.placings.forEach((placing) => {
          newPlacingDict[placing.playerId] = placing;
        });
        setPlacingDict(newPlacingDict);
      }
    } catch (err) {
      postNotification("error", err);
    }
    setSubLoading(false);
    setOriginalLoading(false);
  };

  // Utility functions
  const handleWSConnection = async () => {
    setSocketStatus("Connecting");

    const updateSocketUrl = (endpoint) =>
      setSocketUrl(`${WEBSOCKET_URL}?connectionToken=${endpoint}`);

    let options = {
      eventId: event.id
    };

    if (playerType === "teamplayers") {
      options.connectionType = "eventTeamPlayerPlacings";
    } else {
      options.connectionType = "eventPlayerPlacings";
    }

    const webSocketAuth = await getWSAuth(options);
    updateSocketUrl(webSocketAuth);
  };

  const handleMessages = async (webSocket) => {
    if (webSocket?.lastMessage !== null) {
      const lastMessage = JSON.parse(webSocket.lastMessage?.data);
      if (
        lastMessage?.status === "updated" &&
        lastMessage?.data?.message === "placings updated"
      ) {
        await handleLoad(playerType);
      }
    }
  };

  // Runs whenever a new WS message is received
  useEffect(() => {
    if (webSocket?.lastMessage) {
      const go = async () => {
        try {
          await handleMessages(webSocket);
        } catch (err) {
          postNotification("error", err);
        }
      };

      go();
    }
    // eslint-disable-next-line
  }, [webSocket.lastMessage]);

  useEffect(() => {
    const go = async () => {
      try {
        await handleWSConnection();
      } catch (err) {
        setSocketStatus("Disconnected");
        setSubLoading(false);
      }
    };

    go();
    // eslint-disable-next-line
  }, [playerType]);

  useEffect(() => {
    if (event) {
      if (!organize && event.hidePlacings) {
        var path = newPath({
          active_tab: "overview"
        });
        setActiveTab(0);
        navigate(path, { replace: false });
        return <></>;
      }
      setSubLoading(true);
      handleLoad(event?.teamEvent ? "teamplayers" : "players");
    }
    // eslint-disable-next-line
  }, [activeTab, event]);

  useEffect(() => {
    if (refreshPlayers) {
      setSubLoading(true);
      handleLoad(playerType);
      setRefreshPlayers(false);
    }
    // eslint-disable-next-line
  }, [refreshPlayers]);

  // handle showOverall toggle
  useEffect(() => {
    const check = async () => {
      if (players) {
        const sortedPlayers = sortPlayers(players);
        setPlayers([...sortedPlayers]);
      }
    };
    check();
    // eslint-disable-next-line
  }, [showOverall]);

  useEffect(() => {
    if (!originalLoading) {
      setSubLoading(true);
      handleLoad(playerType);
    }
    // eslint-disable-next-line
  }, [playerType]);

  useEffect(() => {
    const go = async () => {
      await checkStatus();
      window.addEventListener("offline", () => {
        setSocketStatus("Disconnected");
        setSocketUrl(null);
      });
    };
    go();
    // eslint-disable-next-line
  }, []);

  useInterval(() => {
    if (!placingsReady) {
      checkStatus();
    }
  }, 15000);

  return (
    <EventPlacingsDefault
      event={event}
      setEvent={setEvent}
      playerType={playerType}
      setPlayerType={setPlayerType}
      showOverall={showOverall}
      setShowOverall={setShowOverall}
      subLoading={subLoading}
      players={players}
      authContext={authContext}
      organize={organize}
      loading={loading}
      setLoading={setLoading}
      optionsAnchorEl={optionsAnchorEl}
      setOptionsAnchorEl={setOptionsAnchorEl}
      setPlayer={setPlayer}
      placingDict={placingDict}
      league={league}
      player={player}
      playerDict={playerDict}
      setRefreshPlayers={setRefreshPlayers}
      setResetModalOpen={setResetModalOpen}
      placingsReady={placingsReady}
      socketStatus={socketStatus}
      handleWSConnection={handleWSConnection}
      handleLoad={handleLoad}
      setPTypeUpdating={setPTypeUpdating}
      useWebSockets={true}
      setSubLoading={setSubLoading}
    />
  );
}
